import {
  IAccountRespSchema,
  IQuoteItemWithDisplayType,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
  OnQuoteOfferingChange,
  PriceUpliftCalculatorConfig,
  QuoteOfferingForUplift,
} from '@monetize/types/app';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { useOfferingRate } from '../../hooks';
import { getPricesFromProductObject } from '../../routes/ProductCatalog/Offerings/components/Rate/RatePriceUtils';
import { logger } from '../../services/logger';
import {
  buildOriginalItemsMapBySegment,
  calculateUpliftForAllItemsInAllSegments,
  calculateUpliftForSingleItemInAllSegments,
  calculateUpliftedRates,
  collectProductsWithUplifts,
  createUpliftCalculatorObj,
} from '../../utils/upliftPrice.utils';
import { MCard, MCardBody } from '../Monetize';
import { PriceUpliftDuringContract } from './PriceUpliftDuringContract';
import { PriceUpliftOnRenewal } from './PriceUpliftOnRenewal';
import { prepareRateRequestData } from './PricesDrawer/utils';

type PriceUpliftCalculatorProps = {
  account: IAccountRespSchema | Pick<IAccountRespSchema, 'id' | 'accountName'>;
  isReadOnly: boolean;
  quote: IQuoteRespSchema;
  quoteOffering: IQuoteOfferingRespSchema;
  childQuoteOfferings: IQuoteOfferingRespSchema[];
  offeringRate: IRateResSchema;
  quotePrices: IQuotePrice[];
  onOfferingChange: OnQuoteOfferingChange;
  updateQuoteOffering: (
    id: string,
    data: IQuoteOfferingReqSchema,
  ) => Promise<IQuoteOfferingRespSchema | null>;
  setEditMode: (editMode: boolean) => void;
  closeDrawer: () => void;
};

export const PriceUpliftCalculator = ({
  account,
  isReadOnly,
  quote,
  quoteOffering,
  childQuoteOfferings = [],
  offeringRate,
  quotePrices,
  onOfferingChange,
  updateQuoteOffering,
  setEditMode,
  closeDrawer,
}: PriceUpliftCalculatorProps) => {
  const { createOfferingRate, fetchOfferingRate, offeringRatesMap } =
    useOfferingRate(quoteOffering.id);

  const [dataForUpliftCalculator, setDataForUpliftCalculator] =
    useState<PriceUpliftCalculatorConfig>(() =>
      createUpliftCalculatorObj(
        offeringRatesMap,
        quoteOffering,
        childQuoteOfferings,
      ),
    );
  const [originalItemsMapBySegment, setOriginalItemsMapBySegment] = useState<
    Record<string, Record<string, IQuoteItemWithDisplayType>>
  >({});
  const [segmentUpliftMap, setSegmentUpliftMap] = useState(
    () => new Map<string, { upliftAmount: number }>(),
  );
  const [productUpliftMap, setProductUpliftMap] = useState(
    () => new Map<string, Map<string, { upliftAmount: number }>>(),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOfferingRate(quoteOffering.rateId ?? '');
    childQuoteOfferings.forEach((offering) => {
      if (offering.rateId) {
        fetchOfferingRate(offering.rateId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteOffering, childQuoteOfferings]);

  useEffect(() => {
    if (!loading) {
      if (!isEmpty(offeringRatesMap)) {
        const upliftCalculatorObj = createUpliftCalculatorObj(
          offeringRatesMap,
          quoteOffering,
          childQuoteOfferings,
        );
        setDataForUpliftCalculator(upliftCalculatorObj);

        const newMap = buildOriginalItemsMapBySegment(
          childQuoteOfferings,
          offeringRatesMap,
          quoteOffering,
        );
        setOriginalItemsMapBySegment(newMap);
      }
    }
  }, [
    quoteOffering,
    childQuoteOfferings,
    quotePrices,
    offeringRatesMap,
    loading,
  ]);

  /**
   * Calculates and applies price uplift to quote offerings
   * @param upliftPercentage - The percentage to uplift prices by
   * @param offeringToBeUplifted - The quote offering to apply the uplift to
   */
  const handleUpliftForAllItems = (
    upliftPercentage: string,
    offeringToBeUplifted: QuoteOfferingForUplift,
  ) => {
    if (!offeringToBeUplifted) {
      return;
    }

    const offeringIndex = dataForUpliftCalculator.childQuoteOfferings.findIndex(
      (offering) => offering.id === offeringToBeUplifted.id,
    );

    if (offeringIndex === -1) {
      return offeringToBeUplifted.items;
    }

    const updatedState = { ...dataForUpliftCalculator };
    const updatedChildOfferings = [...updatedState.childQuoteOfferings];
    const updatedSegmentUpliftMap = new Map(segmentUpliftMap);

    if (upliftPercentage === '') {
      updatedSegmentUpliftMap.set(offeringIndex.toString(), {
        upliftAmount: 0,
      });
      setSegmentUpliftMap(updatedSegmentUpliftMap);

      handleClearedUplift(updatedChildOfferings, offeringIndex);
    } else {
      updatedSegmentUpliftMap.set(offeringIndex.toString(), {
        upliftAmount: Number(upliftPercentage),
      });
      setSegmentUpliftMap(updatedSegmentUpliftMap);

      calculateUpliftForAllItemsInAllSegments(
        updatedChildOfferings,
        updatedSegmentUpliftMap,
        productUpliftMap,
        originalItemsMapBySegment,
      );
    }

    updatedState.childQuoteOfferings = updatedChildOfferings;
    setDataForUpliftCalculator(updatedState);
  };

  /**
   * Calculates and applies price uplift to a single product in the quote offering
   * @param upliftPercentage - The percentage to uplift price by
   * @param offeringToBeUplifted - The quote offering to apply the uplift to
   * @param productId - The ID of the product to apply uplift to
   */
  const handleUpliftForSingleItem = (
    upliftPercentage: string,
    offeringToBeUplifted: QuoteOfferingForUplift,
    productId: string,
  ) => {
    if (!offeringToBeUplifted) {
      return;
    }

    const offeringIndex = dataForUpliftCalculator.childQuoteOfferings.findIndex(
      (offering) => offering.id === offeringToBeUplifted.id,
    );

    if (offeringIndex === -1) {
      return;
    }

    const updatedProductUpliftMap = new Map(productUpliftMap);
    const segmentKey = offeringIndex.toString();
    const updatedState = { ...dataForUpliftCalculator };
    const updatedChildOfferings = [...updatedState.childQuoteOfferings];

    if (upliftPercentage === '') {
      if (updatedProductUpliftMap.has(segmentKey)) {
        const segmentMap = new Map(updatedProductUpliftMap.get(segmentKey));
        segmentMap.set(productId, { upliftAmount: 0 });
        updatedProductUpliftMap.set(segmentKey, segmentMap);
      } else {
        updatedProductUpliftMap.set(
          segmentKey,
          new Map([[productId, { upliftAmount: 0 }]]),
        );
      }

      setProductUpliftMap(updatedProductUpliftMap);
      handleClearedSingleItemUplift(
        updatedChildOfferings,
        offeringIndex,
        productId,
      );
    } else {
      if (!updatedProductUpliftMap.has(segmentKey)) {
        updatedProductUpliftMap.set(segmentKey, new Map());
      }

      const segmentMap = new Map(updatedProductUpliftMap.get(segmentKey));
      segmentMap.set(productId, { upliftAmount: Number(upliftPercentage) });
      updatedProductUpliftMap.set(segmentKey, segmentMap);
      setProductUpliftMap(updatedProductUpliftMap);

      // Only update the specific product that changed, don't reset all products
      calculateUpliftForSingleItemInAllSegments(
        updatedChildOfferings,
        updatedProductUpliftMap,
        productId,
        segmentUpliftMap,
        originalItemsMapBySegment,
      );
    }

    updatedState.childQuoteOfferings = updatedChildOfferings;
    setDataForUpliftCalculator(updatedState);
  };

  const handleClearedUplift = (
    childOfferings: QuoteOfferingForUplift[],
    offeringIndex: number,
  ) => {
    // Reset all segments to their original values first
    for (let i = 0; i < childOfferings.length; i++) {
      const offering = childOfferings[i];
      const originalItemsMap = originalItemsMapBySegment[offering.id];

      if (originalItemsMap) {
        const originalItems = Object.values(originalItemsMap);
        childOfferings[i] = {
          ...offering,
          items: originalItems,
        };
      }
    }

    const updatedSegmentUpliftMap = new Map(segmentUpliftMap);
    updatedSegmentUpliftMap.set(offeringIndex.toString(), { upliftAmount: 0 });

    const updatedProductUpliftMap = new Map(productUpliftMap);
    const segmentKey = offeringIndex.toString();
    if (updatedProductUpliftMap.has(segmentKey)) {
      const segmentProductMap = updatedProductUpliftMap.get(segmentKey);
      if (segmentProductMap) {
        const clearedSegmentProductMap = new Map(segmentProductMap);
        for (const productId of clearedSegmentProductMap.keys()) {
          clearedSegmentProductMap.set(productId, { upliftAmount: 0 });
        }
        updatedProductUpliftMap.set(segmentKey, clearedSegmentProductMap);
      }
    }
    setProductUpliftMap(updatedProductUpliftMap);

    // Only proceed with cascading calculations if there are any uplifts remaining
    if (updatedSegmentUpliftMap.size > 0) {
      calculateUpliftForAllItemsInAllSegments(
        childOfferings,
        updatedSegmentUpliftMap,
        updatedProductUpliftMap,
        originalItemsMapBySegment,
      );
    }

    if (updatedProductUpliftMap.size > 0) {
      const productsWithUplifts = collectProductsWithUplifts(
        updatedProductUpliftMap,
      );

      for (const productId of productsWithUplifts) {
        calculateUpliftForSingleItemInAllSegments(
          childOfferings,
          updatedProductUpliftMap,
          productId,
          updatedSegmentUpliftMap,
          originalItemsMapBySegment,
        );
      }
    }

    const updatedState = { ...dataForUpliftCalculator };
    updatedState.childQuoteOfferings = childOfferings;
    setDataForUpliftCalculator(updatedState);
  };

  const handleClearedSingleItemUplift = (
    childOfferings: QuoteOfferingForUplift[],
    offeringIndex: number,
    productId: string,
  ) => {
    for (let i = 0; i < childOfferings.length; i++) {
      const offering = childOfferings[i];
      const originalItemsMap = originalItemsMapBySegment[offering.id];

      if (originalItemsMap) {
        const originalItems = Object.values(originalItemsMap);
        childOfferings[i] = {
          ...offering,
          items: originalItems,
        };
      }
    }

    const updatedProductUpliftMap = new Map(productUpliftMap);
    const segmentKey = offeringIndex.toString();
    if (updatedProductUpliftMap.has(segmentKey)) {
      const segmentMap = new Map(updatedProductUpliftMap.get(segmentKey));
      segmentMap.set(productId, { upliftAmount: 0 });
      updatedProductUpliftMap.set(segmentKey, segmentMap);
    } else {
      updatedProductUpliftMap.set(
        segmentKey,
        new Map([[productId, { upliftAmount: 0 }]]),
      );
    }

    if (segmentUpliftMap.size > 0) {
      calculateUpliftForAllItemsInAllSegments(
        childOfferings,
        segmentUpliftMap,
        updatedProductUpliftMap,
        originalItemsMapBySegment,
      );
    }

    // Only proceed with cascading calculations if there are any uplifts remaining
    if (updatedProductUpliftMap.size > 0) {
      const productsWithUplifts = collectProductsWithUplifts(
        updatedProductUpliftMap,
      );

      for (const existingProductId of productsWithUplifts) {
        calculateUpliftForSingleItemInAllSegments(
          childOfferings,
          updatedProductUpliftMap,
          existingProductId,
          segmentUpliftMap,
          originalItemsMapBySegment,
        );
      }
    }

    const updatedState = { ...dataForUpliftCalculator };
    updatedState.childQuoteOfferings = childOfferings;
    setDataForUpliftCalculator(updatedState);
  };

  const handleClearAllUplifts = () => {
    setSegmentUpliftMap(new Map());
    setProductUpliftMap(new Map());

    const updatedState = { ...dataForUpliftCalculator };
    const updatedChildOfferings = [...updatedState.childQuoteOfferings];

    for (let i = 0; i < updatedChildOfferings.length; i++) {
      const offering = updatedChildOfferings[i];
      const originalItemsMap = originalItemsMapBySegment[offering.id];

      if (originalItemsMap) {
        const originalItems = Object.values(originalItemsMap);
        updatedChildOfferings[i] = {
          ...offering,
          items: originalItems,
        };
      }
    }

    updatedState.childQuoteOfferings = updatedChildOfferings;
    setDataForUpliftCalculator(updatedState);
  };

  const handleClearAllProductUpliftsForSegment = (
    offeringToBeCleared: QuoteOfferingForUplift,
  ) => {
    if (!offeringToBeCleared) {
      return;
    }

    const offeringIndex = dataForUpliftCalculator.childQuoteOfferings.findIndex(
      (offering) => offering.id === offeringToBeCleared.id,
    );

    if (offeringIndex === -1) {
      return;
    }

    const updatedProductUpliftMap = new Map(productUpliftMap);
    const segmentKey = offeringIndex.toString();
    const updatedState = { ...dataForUpliftCalculator };
    const updatedChildOfferings = [...updatedState.childQuoteOfferings];

    updatedProductUpliftMap.delete(segmentKey);
    setProductUpliftMap(updatedProductUpliftMap);

    // Reset all segments to their original values first
    for (let i = 0; i < updatedChildOfferings.length; i++) {
      const offering = updatedChildOfferings[i];
      const originalItemsMap = originalItemsMapBySegment[offering.id];

      if (originalItemsMap) {
        const originalItems = Object.values(originalItemsMap);
        updatedChildOfferings[i] = {
          ...offering,
          items: originalItems,
        };
      }
    }

    // Only proceed with cascading calculations if there are any uplifts remaining
    if (segmentUpliftMap.size > 0 || updatedProductUpliftMap.size > 0) {
      calculateUpliftForAllItemsInAllSegments(
        updatedChildOfferings,
        segmentUpliftMap,
        updatedProductUpliftMap,
        originalItemsMapBySegment,
      );

      const productsWithUplifts = collectProductsWithUplifts(
        updatedProductUpliftMap,
      );
      for (const productId of productsWithUplifts) {
        calculateUpliftForSingleItemInAllSegments(
          updatedChildOfferings,
          updatedProductUpliftMap,
          productId,
          segmentUpliftMap,
          originalItemsMapBySegment,
        );
      }
    }

    updatedState.childQuoteOfferings = updatedChildOfferings;
    setDataForUpliftCalculator(updatedState);
  };

  const hasAnyUplifts = segmentUpliftMap.size > 0 || productUpliftMap.size > 0;

  const createRateSequentially = async (
    ratesToBeCreated: {
      data: ReturnType<typeof prepareRateRequestData>;
      parentQuoteOfferingId: string;
      rampOffering: QuoteOfferingForUplift;
    }[],
  ): Promise<
    { createdRate: IRateResSchema; rampOffering: QuoteOfferingForUplift }[]
  > => {
    const createdRatesWithOfferings: {
      createdRate: IRateResSchema;
      rampOffering: QuoteOfferingForUplift;
    }[] = [];

    for (const {
      data,
      parentQuoteOfferingId,
      rampOffering,
    } of ratesToBeCreated) {
      try {
        const { products, ...rest } = data;
        const payload = {
          ...rest,
          prices: getPricesFromProductObject(products),
        };
        const savedRate = await createOfferingRate(
          payload,
          parentQuoteOfferingId,
        );

        if (savedRate) {
          createdRatesWithOfferings.push({
            createdRate: savedRate,
            rampOffering,
          });
        }
      } catch (error) {
        logger.error('Error creating rate:', error);
      }
    }

    setSegmentUpliftMap(new Map());
    setProductUpliftMap(new Map());

    return createdRatesWithOfferings;
  };

  const updateOfferingsWithNewRates = async (
    createdRatesWithOfferings: {
      createdRate: IRateResSchema;
      rampOffering: QuoteOfferingForUplift;
    }[],
  ) => {
    for (const { createdRate, rampOffering } of createdRatesWithOfferings) {
      try {
        await updateQuoteOffering(rampOffering.id, {
          offeringId: rampOffering.offeringId,
          rateId: createdRate.id,
          schedule: {
            startDate: rampOffering.startDate,
            endDate: rampOffering.endDate,
            parentQuoteOfferingId: rampOffering.parentQuoteOfferingId ?? '',
          },
          items: rampOffering.items.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            customDiscountAmountOrPercent: item.customDiscountAmountOrPercent,
            customDiscountType: item.customDiscountType,
            customPrice: item.customPrice
              ? { id: item.customPrice.id, unitAmount: item.customPrice.amount }
              : null,
            position: item.position,
            incrementalQuantity: item.incrementalQuantity,
            options: item.options,
          })),
        });
      } catch (error) {
        logger.error('Error updating offering with new rate:', error);
      }
    }

    onOfferingChange('UPDATE', undefined, undefined, false, true);
  };

  const handleSaveUplift = async () => {
    if (!offeringRate) {
      return;
    }

    const upliftedRatesMap = calculateUpliftedRates(
      dataForUpliftCalculator.childQuoteOfferings,
      offeringRatesMap,
      segmentUpliftMap,
      productUpliftMap,
      quoteOffering.rateId ?? '',
    );

    const ratesToBeCreated: {
      data: ReturnType<typeof prepareRateRequestData>;
      parentQuoteOfferingId: string;
      rampOffering: QuoteOfferingForUplift;
    }[] = [];

    for (const [
      offeringIndexStr,
      offeringPricesUnderProduct,
    ] of upliftedRatesMap.entries()) {
      const rampOfferingIndex = parseInt(offeringIndexStr, 10);
      const rampOffering = dataForUpliftCalculator.childQuoteOfferings
        ? dataForUpliftCalculator.childQuoteOfferings[rampOfferingIndex]
        : null;

      if (rampOffering && offeringPricesUnderProduct.length > 0) {
        const data = prepareRateRequestData(
          account,
          offeringPricesUnderProduct,
          offeringRate,
        );

        ratesToBeCreated.push({
          data,
          parentQuoteOfferingId: quoteOffering.offeringId ?? '',
          rampOffering,
        });
      }
    }

    if (ratesToBeCreated.length > 0) {
      setLoading(true);
      const createdRatesWithOfferings =
        await createRateSequentially(ratesToBeCreated);
      await updateOfferingsWithNewRates(createdRatesWithOfferings);

      setLoading(false);
      closeDrawer();
    } else {
      logger.warn('No rates to be created, skipping');
    }
  };

  /**
   * Syncs back-calculated uplift percentages into the state maps
   * This ensures that when users edit values again, the calculation logic works properly
   */
  const syncBackCalculatedUplifts = (
    segmentUplifts: Record<string, number>,
    productUplifts: Record<string, Record<string, number>>,
  ) => {
    const newSegmentUpliftMap = new Map<string, { upliftAmount: number }>();
    Object.entries(segmentUplifts).forEach(([segmentIndex, upliftAmount]) => {
      if (upliftAmount > 0) {
        newSegmentUpliftMap.set(segmentIndex, { upliftAmount });
      }
    });

    setSegmentUpliftMap(newSegmentUpliftMap);

    // Update product uplift map
    const newProductUpliftMap = new Map<
      string,
      Map<string, { upliftAmount: number }>
    >();
    Object.entries(productUplifts).forEach(([segmentIndex, products]) => {
      const productMap = new Map<string, { upliftAmount: number }>();
      Object.entries(products).forEach(([productId, upliftAmount]) => {
        if (upliftAmount > 0) {
          productMap.set(productId, { upliftAmount });
        }
      });
      if (productMap.size > 0) {
        newProductUpliftMap.set(segmentIndex, productMap);
      }
    });

    setProductUpliftMap(newProductUpliftMap);
  };

  return (
    <>
      <MCard variant="borderless" mb={4}>
        <MCardBody p="2">
          <PriceUpliftDuringContract
            quoteOffering={dataForUpliftCalculator.parentQuoteOffering}
            childQuoteOfferings={dataForUpliftCalculator.childQuoteOfferings}
            quote={quote}
            quotePrices={quotePrices}
            offeringRate={offeringRate}
            calculateUpliftForAllItems={handleUpliftForAllItems}
            calculateUpliftForSingleItem={handleUpliftForSingleItem}
            clearProductUpliftsForSegment={
              handleClearAllProductUpliftsForSegment
            }
            onApply={handleSaveUplift}
            onClearAll={handleClearAllUplifts}
            setEditMode={setEditMode}
            isLoading={loading}
            hasAnyUplifts={hasAnyUplifts}
            syncBackCalculatedUplifts={syncBackCalculatedUplifts}
          />
        </MCardBody>
      </MCard>

      <MCard variant="borderless">
        <MCardBody p="2">
          <PriceUpliftOnRenewal
            isReadOnly={isReadOnly}
            quote={quote}
            quoteOffering={quoteOffering}
            quoteOfferingForUplift={
              dataForUpliftCalculator.childQuoteOfferings.length > 0
                ? dataForUpliftCalculator.childQuoteOfferings[
                    dataForUpliftCalculator.childQuoteOfferings.length - 1
                  ]
                : dataForUpliftCalculator.parentQuoteOffering
            }
            offeringRate={offeringRate}
            quotePrices={quotePrices}
            onOfferingChange={onOfferingChange}
          />
        </MCardBody>
      </MCard>
    </>
  );
};
